import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageTemplate, BackWithOptionsHeader } from '@_widgets/common';
import { Editor, TextSummary } from '@_widgets/content';

import { useContentOptionsModal, extractTitle } from '@_features/content';

import { contentApi, IContentDetail, CONTENT_CONSTANTS, IContent } from '@_entities/content';

import { DownChevronIcon, TopChevronIcon } from '@_shared/icons';
import { HTMLParse, eventPush, toastGenerate } from '@_shared/libs';

import { SkeletonUI } from '@_shared/ui';
import { Typography } from '@_shared/ui/Typography';

import * as S from './DetailPage.style';

export function DetailPage() {
	const { id } = useParams();

	const navigate = useNavigate();

	const [data, setData] = useState<IContentDetail>();

	const textRef = useRef<HTMLDivElement>(null);

	const [isContentOpen, setIsContentOpen] = useState(false);
	const [isContentLengthOver, setIsContentLengthOver] = useState(false);

	const { openContentOptionsModal } = useContentOptionsModal();

	useEffect(() => {
		if (Number.isNaN(Number(id))) {
			navigate(-1);
		}

		contentApi
			.getContent(Number(id))
			.then(({ data: responseData }) => {
				setData(responseData);
			})
			.catch(({ status }) => {
				if (status === 404) {
					toastGenerate({ message: '콘텐츠를 찾을 수 없어요.' });
					navigate(-1);
					navigate('/');
				}
			});
	}, []);

	useEffect(() => {
		setIsContentLengthOver(textRef.current !== null && textRef.current.clientHeight < 300);
	}, [data]);

	if (data === undefined) {
		return (
			<PageTemplate header={false} nav={false}>
				<S.Summary>
					<div
						style={{
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							gap: '4px',
						}}
					>
						<SkeletonUI width="60px" height="20px" borderRadius="8px" />
						<SkeletonUI width="200px" height="42px" borderRadius="8px" />
						<SkeletonUI width="120px" height="20px" borderRadius="8px" />
					</div>
				</S.Summary>

				<S.Contents>
					<Typography type="headline-small" color="brown-primary">
						제목
					</Typography>
					<SkeletonUI width="100%" height="108px" borderRadius="8px" />

					<Typography type="headline-small" color="brown-primary">
						본문
					</Typography>
					<SkeletonUI width="100%" height="108px" borderRadius="8px" />

					<S.HeadlineRow>
						<Typography type="headline-small" color="brown-primary">
							메모
						</Typography>
					</S.HeadlineRow>
					<SkeletonUI width="100%" height="54px" borderRadius="8px" />
				</S.Contents>
			</PageTemplate>
		);
	}

	return (
		<PageTemplate
			header={
				<BackWithOptionsHeader
					onOptionsClick={() =>
						openContentOptionsModal(
							{ ...data, id: Number(id) } as IContent,
							false,
							true,
						)
					}
				/>
			}
			nav={false}
		>
			<S.Summary>
				{data.thumbnailURL && <img src={data.thumbnailURL} alt="thumbnail" />}
				<TextSummary.Container>
					<TextSummary.Folder>{data.folderName}</TextSummary.Folder>
					<TextSummary.Title>{extractTitle(data)}</TextSummary.Title>
					<TextSummary.Link url={data.URL}>{data.siteName}</TextSummary.Link>
				</TextSummary.Container>
			</S.Summary>

			<S.Contents>
				{data.title && (
					<Editor
						title="제목"
						placeholder="제목을 입력해주세요."
						defaultValue={data.title.custom}
						maxLength={CONTENT_CONSTANTS.TITLE_MAX_LENGTH}
						minLength={CONTENT_CONSTANTS.TITLE_MIN_LENGTH}
						onSubmit={(submitData) => {
							contentApi.updateTitle(Number(id), submitData).then(() => {
								window.location.reload();
							});
						}}
						eventPushName="콘텐츠상세.제목.수정.click"
					>
						<Typography type="body-large" color="font-primary">
							{HTMLParse(data.title.custom)}
						</Typography>
					</Editor>
				)}

				{data.content && (
					<>
						<Typography type="headline-small" color="brown-primary">
							본문
						</Typography>

						<S.ContentText
							ref={textRef}
							isFullBorderRadius={isContentLengthOver}
							isContentOpen={isContentOpen}
						>
							<Typography type="body-large" color="font-primary">
								{HTMLParse(data.content)}
							</Typography>
						</S.ContentText>
						{!isContentLengthOver && (
							<S.ContentTextOpenButton
								onClick={() => {
									if (!isContentOpen) {
										eventPush('콘텐츠상세.본문.펼치기.click');
									}
									setIsContentOpen((prev) => !prev);
								}}
							>
								<Typography type="title-large" color="font-tertiary">
									{isContentOpen ? '접기' : '펼치기'}
								</Typography>
								{isContentOpen ? <TopChevronIcon /> : <DownChevronIcon />}
							</S.ContentTextOpenButton>
						)}
					</>
				)}

				<Editor
					title="메모"
					placeholder="메모를 입력해 주세요."
					defaultValue={data.memo ?? ''}
					maxLength={CONTENT_CONSTANTS.MEMO_MAX_LENGTH}
					minLength={CONTENT_CONSTANTS.MEMO_MIN_LENGTH}
					onSubmit={(submitData) => {
						contentApi.updateMemo(Number(id), submitData).then(() => {
							window.location.reload();
						});
					}}
					eventPushName="콘텐츠상세.메모.수정.click"
				>
					<Typography
						type="body-large"
						color={
							data.memo === '' || data.memo === null
								? 'font-tertiary'
								: 'font-primary'
						}
					>
						{HTMLParse(
							data.memo === '' || data.memo === null
								? '작성된 메모가 없어요.'
								: data.memo,
						)}
					</Typography>
				</Editor>
			</S.Contents>
		</PageTemplate>
	);
}
